// Fluid sizing helpers for the User Management V2 module.
//
// Why this exists: every size in this module was hard-coded in px. Browser
// zoom changes the CSS viewport size (zooming OUT makes the CSS viewport
// bigger, zooming IN makes it smaller) but a px value never moves, so the
// exact same layout looks "too big"/cramped at 100% zoom and only looks
// right once a user zooms out to ~80%. vw/vh units scale WITH the viewport,
// which cancels that out: shrink the viewport (zoom in) and a vw value
// shrinks by the same ratio, so the on-screen size stays visually constant
// across zoom levels. That's what these helpers give every size in the
// module, with a clamp() floor/ceiling so it never gets unreadably small on
// a narrow window or absurdly large on an ultra-wide one.
//
// REF_WIDTH/REF_HEIGHT are the CSS viewport size this design was tuned
// against (a common wide admin screen at ~80% browser zoom, e.g. a physical
// 2240x1400 display). A px value converts to "the same fraction of that
// reference viewport" expressed in vw/vh, so at that exact viewport size
// fluid(16) renders as exactly 16px, and it scales proportionally above/
// below that.
const REF_WIDTH = 2800;
const REF_HEIGHT = 1600;

/**
 * Horizontal-ish sizing (fonts, paddings, gaps, widths, radii) that should
 * track viewport WIDTH so it stays visually constant across browser zoom
 * levels on the same screen.
 *
 * @param {number} px - the original fixed-px value
 * @param {{minRatio?: number, maxRatio?: number}} [opts]
 * @returns {string} a CSS clamp() expression
 */
export const fluid = (px, { minRatio = 0.72, maxRatio = 1.15 } = {}) => {
    const vw = (px / REF_WIDTH) * 100;
    const min = +(px * minRatio).toFixed(2);
    const max = +(px * maxRatio).toFixed(2);
    return `clamp(${min}px, ${vw.toFixed(4)}vw, ${max}px)`;
};

/**
 * Vertical sizing (card/section heights, chart heights) that should track
 * viewport HEIGHT so tall/short screens (and zoom) get proportionally
 * sized blocks instead of a fixed px height that overflows or leaves a gap.
 *
 * @param {number} px - the original fixed-px value
 * @param {{minRatio?: number, maxRatio?: number}} [opts]
 * @returns {string} a CSS clamp() expression
 */
export const fluidVh = (px, { minRatio = 0.72, maxRatio = 1.15 } = {}) => {
    const vh = (px / REF_HEIGHT) * 100;
    const min = +(px * minRatio).toFixed(2);
    const max = +(px * maxRatio).toFixed(2);
    return `clamp(${min}px, ${vh.toFixed(4)}vh, ${max}px)`;
};
